import { lighten } from 'polished';
import * as React from 'react';
import styled from '@emotion/styled';

import ShareIcon from './icons/share';
import { colors } from '../styles/colors';

const ShareBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  border: ${lighten('0.2', colors.midgrey)} 1px solid;
  color: ${colors.midgrey};
  font-size: 1.2rem;
  line-height: 1;
  font-weight: 500;
  border-radius: 20px;
  background: transparent;
  cursor: pointer;
  transition: all ease 0.2s;
  white-space: nowrap;

  :hover {
    border-color: ${colors.blue};
    color: ${colors.blue};
  }
`;

async function writeUrlToClipboard(url: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.pointerEvents = 'none';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

export interface ArticleShareLinkButtonProps {
  /** Page path (e.g. from `location.pathname`); clipboard uses `origin + pathname` when clicked. */
  pathname: string;
  className?: string;
}

function absoluteUrlFromPathname(pathname: string): string {
  return new URL(pathname, window.location.origin).href;
}

const ArticleShareLinkButton: React.FC<ArticleShareLinkButtonProps> = ({ pathname, className }) => {
  const [label, setLabel] = React.useState('Share');

  const onClick = () => {
    void (async () => {
      const url = absoluteUrlFromPathname(pathname);
      const ok = await writeUrlToClipboard(url);
      if (ok) {
        setLabel('Copied!');
        window.setTimeout(() => setLabel('Share'), 2000);
      } else {
        setLabel('Copy failed');
        window.setTimeout(() => setLabel('Share'), 2000);
      }
    })();
  };

  return (
    <ShareBtn
      type="button"
      className={className}
      onClick={onClick}
      aria-label="Copy article link to clipboard"
    >
      <ShareIcon />
      {label}
    </ShareBtn>
  );
};

export default ArticleShareLinkButton;
